import React from 'react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Bell, X, Calendar, MessageCircle, AlertTriangle,
  Phone, Info, CheckCheck, ChevronLeft
} from 'lucide-react';

const NotificationPanel = ({ 
  isOpen, 
  onClose, 
  notifications, 
  unreadCount,
  onMarkAsRead,
  onMarkAllAsRead,
  connected 
}) => {
  if (!isOpen) return null;

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'appointment':
        return <Calendar className="w-5 h-5 text-blue-500" />;
      case 'message':
        return <MessageCircle className="w-5 h-5 text-indigo-500" />;
      case 'issue':
        return <AlertTriangle className="w-5 h-5 text-rose-500" />;
      case 'callback':
        return <Phone className="w-5 h-5 text-amber-500" />;
      default:
        return <Info className="w-5 h-5 text-slate-500" />;
    }
  };

  const getNotificationBg = (type, read) => {
    if (read) return 'bg-white';
    switch (type) {
      case 'appointment':
        return 'bg-blue-50';
      case 'message':
        return 'bg-indigo-50';
      case 'issue':
        return 'bg-rose-50';
      case 'callback':
        return 'bg-amber-50';
      default:
        return 'bg-slate-50';
    }
  };

  return (
    <div className="fixed inset-0 z-[100]">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl flex flex-col safe-area-inset">
        {/* Header - with safe area padding for PWA */}
        <div 
          className="flex items-center justify-between p-4 border-b border-slate-200 bg-white"
          style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
        >
          {/* Close button on the LEFT for mobile accessibility */}
          <button
            onClick={onClose}
            className="flex items-center gap-2 p-2 -ml-2 rounded-lg hover:bg-slate-100 text-slate-600 font-medium"
          >
            <ChevronLeft className="w-5 h-5" />
            <span className="text-sm">Retour</span>
          </button>
          
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-slate-600" />
            <h2 className="font-semibold text-slate-900">Notifications</h2>
            {unreadCount > 0 && (
              <Badge className="bg-rose-500 text-white">{unreadCount}</Badge>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {/* Connection status - static, not blinking */}
            {connected && (
              <div className="w-2 h-2 rounded-full bg-emerald-500" title="Connecté" />
            )}
            {unreadCount > 0 && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={onMarkAllAsRead}
                className="text-slate-500 hover:text-slate-700 p-2"
              >
                <CheckCheck className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Aucune notification</p>
              <p className="text-slate-400 text-sm mt-1">Vous serez notifié en temps réel</p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-4 hover:bg-slate-50 transition-colors cursor-pointer ${getNotificationBg(notification.type, notification.read)}`}
                  onClick={() => !notification.read && onMarkAsRead(notification.id)}
                >
                  <div className="flex gap-3">
                    <div className="flex-shrink-0 mt-0.5">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm ${notification.read ? 'text-slate-600' : 'text-slate-900 font-medium'}`}>
                          {notification.title}
                        </p>
                        {!notification.read && (
                          <div className="w-2 h-2 rounded-full bg-blue-500 flex-shrink-0 mt-1.5" />
                        )}
                      </div>
                      <p className="text-sm text-slate-500 mt-0.5 line-clamp-2">
                        {notification.message}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {format(new Date(notification.created_at), "d MMM à HH:mm", { locale: fr })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {connected && (
          <div 
            className="p-3 border-t border-slate-200 bg-emerald-50"
            style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
          >
            <p className="text-xs text-emerald-700 text-center flex items-center justify-center gap-1">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              Notifications en temps réel actives
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationPanel;
